import { useCallback } from "react";

import { getReferenceString, QueryTypes } from "@medplum/core";
import { Organization, PlanDefinition } from "@medplum/fhirtypes";
import {
  AsyncAutocomplete,
  AsyncAutocompleteOption,
  AsyncAutocompleteProps,
  useMedplum,
} from "@medplum/react";

import { Pill, Stack } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { QuiltedHealthUsageContextTypes } from "@/fhir/enums/quilted-health";
import { formatLabOrdersDisplay } from "@/settings/utils/labs";
import { getOrganizationReference, getResourcesFromBundle } from "@/utils/medplum";

interface LabOrderTemplateSearchInputProps
  extends Omit<AsyncAutocompleteProps<PlanDefinition>, "toOption" | "loadOptions"> {
  accounts: string[];
  practiceId?: string;
  performingLab?: Organization;
  onRemove?: (item: PlanDefinition) => void;
}

export default function LabOrderTemplateSearchInput({
  accounts,
  practiceId,
  performingLab,
  onRemove,
  ...props
}: LabOrderTemplateSearchInputProps) {
  const medplum = useMedplum();

  const toOption = useCallback(
    (template: PlanDefinition) => ({
      value: getReferenceString(template) ?? "",
      label: template.title ?? "",
      resource: template,
    }),
    [],
  );

  const loadOptions = useCallback(
    async (query: string) => {
      const params: QueryTypes = {
        "title:contains": query,
        _compartment: getOrganizationReference(practiceId),
        "context-type": QuiltedHealthUsageContextTypes.LabOrderTemplates,
        "depends-on": accounts.join(","),
      };

      const performingLabReference = performingLab ? getReferenceString(performingLab) : undefined;
      if (performingLabReference) {
        // If performingLab is not in accounts, return empty array
        // to prevent searching for templates that are not available
        if (!accounts.includes(performingLabReference)) return [];
        params["depends-on"] = performingLabReference;
      }

      const { bundle } = await medplum.searchResources("PlanDefinition", params);

      return getResourcesFromBundle("PlanDefinition", bundle) as PlanDefinition[];
    },
    [medplum, performingLab, practiceId, accounts],
  );

  const handleRemove = useCallback(
    (onRemovePill: () => void, item: PlanDefinition) => {
      onRemovePill();
      onRemove?.(item);
    },
    [onRemove],
  );

  return (
    <AsyncAutocomplete<PlanDefinition>
      maxValues={1}
      toOption={toOption}
      loadOptions={loadOptions}
      itemComponent={(props) => <LabOrderTemplateItem {...props} />}
      pillComponent={({ onRemove, item }) => (
        <Pill
          withRemoveButton
          onRemove={() => handleRemove(onRemove, item.resource)}
          removeButtonProps={{ "aria-label": "Remove Template" }}
        >
          {item.label}
        </Pill>
      )}
      {...props}
    />
  );
}

function LabOrderTemplateItem({ resource }: AsyncAutocompleteOption<PlanDefinition>) {
  const [firstThreeLabs, remainingLabsCount] = formatLabOrdersDisplay(resource.action);
  return (
    <Stack gap="xs">
      <TextWrapper variant="bold">{resource.title}</TextWrapper>
      <TextWrapper>
        Tests: {firstThreeLabs} <strong>{remainingLabsCount}</strong>
      </TextWrapper>
    </Stack>
  );
}
